import {every, filter, find, forEach, forOwn, includes, isEmpty} from 'lodash';
import {createSelector} from 'reselect';

import {
  AUTOCOMPLETE_PARENT_ID_SEPARATOR,
  CORE_ID_SEPARATOR,
} from './filtering_constants';
import {getServiceUrls} from 'src/js/containers/filter_and_search/filter_and_search_selectors';
import {FilterParser} from 'src/js/lib/filtering';

const getAutocompleteValues = (state, props) =>
  state[props.filteringStateId].autocompleteValues;
const getStateSchema = (state, props) => state[props.filteringStateId].schema;
const getDataSets = (state, props) => state[props.configStateId].dataSets;
const getHierarchyChildren = (state, props) =>
  state[props.filteringStateId].hierarchyChildren;
const getSelectedFilters = (state, props) =>
  state[props.filteringStateId].selectedFilters;
const getOpenedFilters = (state, props) =>
  state[props.filteringStateId].openedFilters;
const getUserFiltersSelector = (state, props) =>
  state[props.filteringStateId].userFilters;
const getUserFiltersLoadingSelector = (state, props) =>
  state[props.filteringStateId].userFiltersLoading;
const getPropsNode = (state, props) => props.node;
const getPropsParentName = (state, props) => props.parentName;
const getPropsSchema = (state, props) => props.schema;
const getPropsIsClickable = (state, props) => props.isClickable;
const getLastFocusedFilter = (state, props) =>
  state[props.filteringStateId].lastFocusedFilterId;

const STATIC_EMPTY_ARRAY = [];
const STATIC_EMPTY_OBJECT = {};
const STATIC_CONST_ZERO = 0;

const combinerIsFilterFocused = (focusedFilterId, node) =>
  focusedFilterId === node.id;

export const makeIsFilterFocused = () =>
  createSelector([getLastFocusedFilter, getPropsNode], combinerIsFilterFocused);

const combinerGetSelectedFilter = (
  selectedFilters,
  propsNode,
  parentName,
  propsIsClickable,
  propsSchema
) => {
  const type = propsIsClickable
    ? propsSchema.getFilterByName(parentName).getType()
    : propsNode.getType();
  const filterInstance = FilterParser.instantiateFilterFromSchema(
    propsNode,
    type,
    parentName
  );
  return find(selectedFilters, (selectedFilter) =>
    selectedFilter.isEqual(filterInstance)
  );
};

const combinerGetOpenedFilter = (
  openedFilters,
  propsNode,
  parentName,
  propsIsClickable,
  propsSchema
) => {
  const type = propsIsClickable
    ? propsSchema.getFilterByName(parentName).getType()
    : propsNode.getType();
  const filterInstance = FilterParser.instantiateFilterFromSchema(
    propsNode,
    type,
    parentName
  );
  return openedFilters[filterInstance.property];
};

export const getNotEmptySelectedFilters = createSelector(
  [getSelectedFilters],
  (selectedFilters) =>
    filter(
      selectedFilters,
      (selectedFilter) => selectedFilter && selectedFilter.value != null
    )
);

const getSelectedFilter = createSelector(
  [
    getNotEmptySelectedFilters,
    getPropsNode,
    getPropsParentName,
    getPropsIsClickable,
    getPropsSchema,
  ],
  combinerGetSelectedFilter
);

export const makeGetSelectedFilter = () =>
  createSelector(
    [
      getNotEmptySelectedFilters,
      getPropsNode,
      getPropsParentName,
      getPropsIsClickable,
      getPropsSchema,
    ],
    combinerGetSelectedFilter
  );

export const makeGetOpenedFilter = () =>
  createSelector(
    [
      getOpenedFilters,
      getPropsNode,
      getPropsParentName,
      getPropsIsClickable,
      getPropsSchema,
    ],
    combinerGetOpenedFilter
  );

export const getLoadedHierarchyChildrenIds = createSelector(
  [getHierarchyChildren],
  (hierarchyChildren) => {
    const resultIds = [];
    forOwn(hierarchyChildren, (parentCollection, key) => {
      const coreId =
        key.indexOf(AUTOCOMPLETE_PARENT_ID_SEPARATOR) > -1
          ? key.substring(0, key.indexOf(AUTOCOMPLETE_PARENT_ID_SEPARATOR))
          : key;
      forEach(parentCollection.options, (option) => {
        resultIds.push(coreId + CORE_ID_SEPARATOR + option.getId());
      });
    });

    return resultIds;
  }
);

export const getLoadedHierarchyChildrenLabels = createSelector(
  [getHierarchyChildren],
  (hierarchyChildren) => {
    const resultLabels = {};
    forOwn(hierarchyChildren, (parentCollection) => {
      forEach(parentCollection.getOptions(), (option) => {
        resultLabels[option.getId()] = option.getLabel();
      });
    });

    return resultLabels;
  }
);

const getUserFilters = createSelector(
  [getUserFiltersSelector, getServiceUrls],
  (userFilters, serviceUrls) =>
    userFilters && userFilters[serviceUrls.userFilterType]
      ? userFilters[serviceUrls.userFilterType]
      : STATIC_EMPTY_OBJECT
);

const getUserFiltersLoading = createSelector(
  [getUserFiltersLoadingSelector, getServiceUrls],
  (userFiltersLoading, serviceUrls) =>
    userFiltersLoading && userFiltersLoading[serviceUrls.userFilterType]
      ? userFiltersLoading[serviceUrls.userFilterType]
      : STATIC_CONST_ZERO
);

export const getSerializedFilter = createSelector(
  [getSelectedFilters],
  (selectedFilters) => FilterParser.toString(selectedFilters)
);

export const getUserFiltersByType = createSelector(
  [getUserFilters],
  (userFilters) => userFilters
);

export const getUserFiltersLoadingByType = createSelector(
  [getUserFiltersLoading],
  (userFiltersLoading) => userFiltersLoading
);

export const getDataSetIds = createSelector([getStateSchema], (schema) =>
  schema.getDataSetIds()
);

export const getDefaultValueFilters = createSelector(
  [getStateSchema],
  (schema) => schema.getDefaultValueFilters()
);

export const areDataSetsLoaded = createSelector(
  [getStateSchema, getDataSets, getDataSetIds],
  (schema, dataSets, datasetIds) =>
    schema && schema.isLoaded() && every(datasetIds, (id) => dataSets[id])
);

const getFilterAutocompleteValues = createSelector(
  [getAutocompleteValues, getPropsNode],
  (autocompleteValues, propsNode) =>
    autocompleteValues && propsNode && propsNode.getHierarchyType()
      ? autocompleteValues[propsNode.getHierarchyType()]
      : STATIC_EMPTY_OBJECT
);

const combinerGetSelectedAutocompleteValues = (
  selectedFilter,
  autocompleteValues
) =>
  selectedFilter && !isEmpty(autocompleteValues)
    ? filter(autocompleteValues.getMappedOptions(), (item) =>
        includes(selectedFilter.getValue(), item.value)
      )
    : STATIC_EMPTY_ARRAY;

export const makeGetSelectedAutocompleteValues = () =>
  createSelector(
    [getSelectedFilter, getFilterAutocompleteValues],
    combinerGetSelectedAutocompleteValues
  );
